import { FormEvent, RefObject, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import api from '../../../../Component/axios_Interceptor/api';
import { SERVER_ADDRESS } from '../../../../Component/server';
import ModalCloseButton from '../../../../Component/UI/ModalCloseButton';
import RefundAccountFields, { useRefundAccount } from './RefundAccountFields';

export default function RefundAccountDialog({ onClose, onSaved, returnFocusRef }: {
  onClose: () => void; onSaved: () => void; returnFocusRef: RefObject<HTMLButtonElement | null>;
}) {
  const account = useRefundAccount();
  const dialog = useRef<HTMLDialogElement>(null);
  const sending = useRef(false);
  const active = useRef(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    active.current = true;
    const element = dialog.current!, trigger = returnFocusRef.current;
    if (element.showModal) element.showModal(); else element.setAttribute('open', '');
    return () => { active.current = false; element.close?.(); trigger?.focus(); };
  }, [returnFocusRef]);
  useEffect(() => { if (account.ready) dialog.current?.querySelector('select')?.focus(); }, [account.ready]);
  const close = () => { if (!sending.current) onClose(); };
  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (sending.current) return;
    sending.current = true; setSaving(true); setError('');
    try {
      const payload = account.payload();
      if (!payload) throw new Error('은행·계좌번호·예금주를 모두 입력해주세요.');
      const { data } = await api.patch(`${SERVER_ADDRESS}user/refund-account`, payload);
      if (!Number.isSafeInteger(data?.refund_account?.version) || data.refund_account.version <= payload.expected_version) {
        throw new Error('환불 계좌 저장 결과를 확인하지 못했습니다. 다시 조회해주세요.');
      }
      if (active.current) onSaved();
    } catch (failure: any) {
      setError(typeof failure?.response?.data?.message === 'string' ? failure.response.data.message
        : failure?.message || '환불 계좌를 저장하지 못했습니다. 다시 시도해주세요.');
    } finally { sending.current = false; setSaving(false); }
  };
  return createPortal(<Dialog ref={dialog} aria-labelledby="request-refund-account-title"
    onCancel={event => { event.preventDefault(); event.stopPropagation(); close(); }}>
    <header><h2 id="request-refund-account-title">환불 계좌 등록</h2><ModalCloseButton aria-label="환불 계좌 등록 닫기" disabled={saving} onClick={close}/></header>
    <form onSubmit={save}>
      <div className="account-body" aria-busy={saving}>
        <RefundAccountFields state={account} disabled={saving} prefix="request-refund" />
        {error && <p role="alert">{error}</p>}
        {error && <button type="button" disabled={saving} onClick={() => { setError(''); void account.load(); }}>환불 계좌 다시 조회</button>}
      </div>
      <footer><button type="button" disabled={saving} onClick={close}>닫기</button><button type="submit" className="save" disabled={saving || !account.ready}>{saving ? '저장 중…' : '저장하고 환불 요청'}</button></footer>
    </form>
  </Dialog>, document.body);
}

const Dialog = styled.dialog`
  box-sizing:border-box;width:min(480px,calc(100% - 32px));max-height:calc(100dvh - 32px);padding:0;
  border:1px solid var(--user-border);border-radius:12px;background:var(--user-surface);color:var(--user-text);overflow:hidden;font:inherit;
  &[open]{display:flex;flex-direction:column;} &::backdrop{background:#0009;}
  >header{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:16px 20px;border-bottom:1px solid var(--user-border);}
  h2{margin:0;font-size:20px;} form{display:flex;flex-direction:column;min-height:0;}
  .account-body{min-height:0;overflow-y:auto;padding:20px;}
  .account-body>section{padding-top:0;border-top:0;} .account-body h2{font-size:16px;}
  input,select{font-size:16px;}
  [role=alert]{color:var(--user-error);line-height:1.5;}
  footer{display:flex;justify-content:flex-end;gap:10px;padding:16px 20px;border-top:1px solid var(--user-border);}
  form button{min-height:44px;padding:10px 16px;border:1px solid var(--user-border);border-radius:8px;font:inherit;background:var(--user-surface-raised);color:var(--user-text);cursor:pointer;}
  form button:hover:not(:disabled){background:var(--user-accent-soft);} form button:disabled{opacity:.5;cursor:default;}
  button:focus-visible{outline:2px solid var(--user-accent);outline-offset:2px;}
  footer .save{background:var(--user-button);color:#fff;border-color:var(--user-button);} footer .save:hover:not(:disabled){background:var(--user-accent);}
`;
